/**
 * Purchase CTA layout helper
 *
 * Central rule for deciding which CTAs render in the storefront category
 * list, featured carousel, and product page rows. All three surfaces ask
 * the same question — "given the org config and the user's eligibility /
 * insurance state, should we show Connect Insurance alongside the purchase
 * button?" — so the visibility logic lives here instead of being
 * re-derived inline.
 *
 * Callers also pick a visual arrangement via the `layout` input:
 *
 *  - `"inline"` (category list): Book Now (w-fit) + Connect Insurance
 *    (flex-1) share a row.
 *  - `"stacked"` (featured carousel, product page): Connect Insurance
 *    (w-full) sits above Book Now (w-full).
 *
 * Visibility rules (eligibility system = `storefront_offboard_consumers`):
 *
 * | Eligibility mode | Result       | Insurance connected | Row (inline)                            |
 * | ---------------- | ------------ | ------------------- | --------------------------------------- |
 * | ON               | not_checked  | —                   | "See if eligible" (full)                |
 * | ON               | passed       | no                  | Book Now (fit) + Connect Insurance      |
 * | ON               | passed       | yes                 | Book Now (full)                         |
 * | ON               | failed       | —                   | View Details + ineligible banner        |
 * | OFF              | —            | no                  | Book Now (fit) + Connect Insurance      |
 * | OFF              | —            | yes                 | Book Now (full)                         |
 *
 * `stacked` layouts render the same set of buttons, just wrapped in a
 * column with both buttons at `w-full` when Connect Insurance is visible.
 *
 * The "See if eligible" and "View Details + banner" rows are rendered by
 * `EligibilityButton` itself based on `eligibilityCheckResult`; this
 * helper only describes the outer row (purchase button width, whether to
 * render Connect Insurance alongside it, and the flex direction).
 */

import type { EligibilityCheckResultType } from "@frontend/lib/stores/slices/storefrontSlice";

export type PurchaseCtaLayout = {
  /**
   * Render the Connect Insurance button alongside the purchase button.
   * When false, the purchase button takes the row by itself.
   */
  showConnectInsurance: boolean;
  /**
   * Visual arrangement when both buttons are shown. Mirrors the caller's
   * `layout` input — kept on the returned object so consumers can switch
   * on a single source of truth instead of threading the prop separately.
   */
  layout: "inline" | "stacked";
  /**
   * Width the purchase (Book Now / See if eligible / View Details) button
   * should use.
   *
   * - `"fit"` is only returned for `inline` layouts when Connect Insurance
   *   is also visible (so the pair fits side-by-side).
   * - `"full"` otherwise — stacked layouts always render both buttons
   *   full-width, and single-button rows stretch.
   */
  bookNowWidth: "fit" | "full";
  /**
   * Uniform Tailwind height class applied to every CTA in the row so the
   * row reads as a single visual unit. Inline rows (category list) use a
   * shorter pill at `h-[54px]`; stacked rows (featured carousel, product
   * page) use `h-[64px]`.
   */
  buttonHeightClass: "h-[54px]" | "h-[64px]";
  /**
   * Height class for the *outer container* of the Connect Insurance
   * `MovingBorderButton`. The moving-border wrapper has a 3px padding
   * ring around its inner black pill (to render the animated border),
   * so its visible surface is 6px shorter than the container height.
   *
   * This value is always `buttonHeightClass` + 6px so the visible black
   * pill lines up with the neighbouring Book Now / See if eligible
   * buttons pixel-for-pixel.
   */
  connectInsuranceOuterHeightClass: "h-[60px]" | "h-[70px]";
};

export function getPurchaseCtaLayout(args: {
  /** Product is insurable (not self-pay-only AND org supports insured procedures). */
  isInsurable: boolean;
  /** Insurance has been fully verified and saved to the store. */
  isInsuranceConnected: boolean;
  /** Price query is currently in flight — hide Connect Insurance to avoid flicker. */
  isFetchingPrice: boolean;
  /** Latest eligibility result from the global store. */
  eligibilityResult: EligibilityCheckResultType;
  /** Org uses the `storefront_offboard_consumers` eligibility flow. */
  isEligibilitySystemOn: boolean;
  /** Visual arrangement requested by the caller (category list = inline, carousel/PDP = stacked). */
  layout: "inline" | "stacked";
}): PurchaseCtaLayout {
  const {
    isInsurable,
    isInsuranceConnected,
    isFetchingPrice,
    eligibilityResult,
    isEligibilitySystemOn,
    layout,
  } = args;

  const hasFailedEligibility = eligibilityResult === "failed";
  const hasPassedEligibility = eligibilityResult === "passed";

  // Eligibility-gated orgs only offer Connect Insurance AFTER the user
  // has passed the eligibility check. Before that, we show just the
  // "See if eligible" CTA (rendered internally by EligibilityButton) so
  // the single required next step is unambiguous. If eligibility fails,
  // the user is off-boarded and we hide Connect Insurance too.
  const eligibilityAllowsInsurance = isEligibilitySystemOn
    ? hasPassedEligibility
    : true;

  const showConnectInsurance =
    isInsurable &&
    !isInsuranceConnected &&
    !isFetchingPrice &&
    !hasFailedEligibility &&
    eligibilityAllowsInsurance;

  // Stacked rows always render both buttons at full width. Only the
  // inline layout needs the purchase button to hug its content so the
  // Connect Insurance pill can grow to fill the remaining width.
  const bookNowWidth: "fit" | "full" =
    showConnectInsurance && layout === "inline" ? "fit" : "full";

  // Height pairs 1:1 with layout: compact inline rows (category list) use
  // a 54px pill so several fit in a scroll column; stacked rows (carousel
  // + product page) use the marketing-scale 64px pill.
  const buttonHeightClass: "h-[54px]" | "h-[64px]" =
    layout === "inline" ? "h-[54px]" : "h-[64px]";

  // The moving-border wrapper used for Connect Insurance adds 3px of
  // padding on every side so the animated border has room to render —
  // without compensation its visible black pill is 6px shorter than the
  // container. Bumping the outer container by exactly 6px keeps the
  // visible pill flush with Book Now.
  const connectInsuranceOuterHeightClass: "h-[60px]" | "h-[70px]" =
    layout === "inline" ? "h-[60px]" : "h-[70px]";

  return {
    showConnectInsurance,
    layout,
    bookNowWidth,
    buttonHeightClass,
    connectInsuranceOuterHeightClass,
  };
}
